import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
// Common
// Others
import { Editor, EditorState } from 'draft-js';
import { useStore } from '../../store/store';
import { displayTitleIndex, getTitlesConfig, numberedTitleLevels, selectLevelTitle, titleDisplayIndex } from '../../utils/titles';
import { getTitleLayout, getTitleStyle, getTitleTextStyle } from './titleUtils';
import "./Title.css"
import ReactDOM from 'react-dom';
import { AddVariableInContentTextRichEditor, titleDecorator, handleBeforeInput } from '../RichEditor/utils';
import ContextualMenu from '../ContextualMenu/ContextualMenu';
import { MENU_ANCESTORS, TITLES, viewerTypes } from '../../constants/constants';
import { EditorProvider } from '../../contexts/EditorContext';
import { usePreviewContext } from '../../contexts/PreviewContext';
import NavbarSelector from '../Layout/NavbarSelector';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { Switch } from 'antd';
import Tooltip from '../common/Tooltip';
import { calculateOptimalLineHeight } from '../../utils/styles';
import { useTranslation } from 'react-i18next';
import { patchBoxContent } from '../../collab/useEditorCollab';
import BoxActionsPanel from '../common/BoxActionsPanel';
import InsertionControls from '../common/InsertionControls';
import LabelledRow from '../common/LabelledRow';
import BoxFormControlsPortal from '../RightSide/BoxFormControlsPortal';
// Store selector
const selector = (id, type) => (state) => ({
  updateBox: state.updateBox,
  variables: state.opportunitieVariables,
  configuration: state.configuration,
  configurationType: state.configuration[type],
  setBoxMovable: state.setBoxMovable,
  separator: state.configuration.separatorTitle,
  gridConfiguration: state.gridConfiguration,
  titleTypeConfig: getTitlesConfig(state.configuration),
  isTemplate: state.currentDocument.type === 'template',
  setEventManagerEnabled: state.setEventManagerEnabled,
  isSelected: state.selectedBoxId === id,
  fromPdf: state.fromPdf,
  state,
  boxes: state.boxes,
  columns: state.columns,
});

const draft = () => (state) => ({
  updateIndexTitle: state.updateIndexTitle,
  updateBox: state.updateBox,
  defaultConfiguration: state.configuration,
  toggleTitleNumbering: state.toggleTitleNumbering,
  isTemplate: state.currentDocument.type === 'template',
});

const Title = ({ box, drawMode }) => {
  const editorRef = useRef(null);
  const boundingBoxRef = useRef(null);
  const {
    updateBox,
    configuration,
    configurationType,
    separator,
    setBoxMovable,
    titleTypeConfig,
    gridConfiguration,
    setEventManagerEnabled,
    isSelected,
    fromPdf,
    state,
    boxes,
    columns,
  } = useStore(selector(box.id, box.type));
  const [editorState, setEditorState] = useState(EditorState.createWithContent(
    box.content.editorState.getCurrentContent(),
    titleDecorator
  ));

  const [defaultSelection, setDefaultSelection] = useState(null);
  const saveBoxImmediate = useCallback((editorState) => {
    if (boundingBoxRef.current) {
      updateBox(box.id, (box) => {
        box.height = boundingBoxRef?.current.firstChild.clientHeight;
        box.content.editorState = EditorState.createWithContent(
          editorState.getCurrentContent(),
          titleDecorator
        );
      });
    }
  }, [box?.id, updateBox]);

  const cleanupRef = useRef(editorState);
  const consumeSkipBoxSaveOnUnselect = useStore((state) => state.consumeSkipBoxSaveOnUnselect);
  const documentContentSyncTick = useStore((state) => state.documentContentSyncTick);

  useEffect(() => {
    cleanupRef.current = editorState
  });

  useLayoutEffect(() => {
    return () => {
      if (isSelected && cleanupRef.current) {
        if (consumeSkipBoxSaveOnUnselect(box.id)) return;
        saveBoxImmediate(cleanupRef.current);
      }
    };
  }, [isSelected, saveBoxImmediate, box.id, consumeSkipBoxSaveOnUnselect]);

  useEffect(() => {
    if (!isSelected) {
      setEditorState(EditorState.createWithContent(
        box.content.editorState.getCurrentContent(),
        titleDecorator
      ))
    }
  }, [box?.content?.editorState, isSelected])

  useEffect(() => {
    setEditorState(EditorState.createWithContent(
      box.content.editorState.getCurrentContent(),
      titleDecorator
    ));
  }, [documentContentSyncTick, box.id])

  // Gérer le surlignage des résultats de recherche pour les titres
  const { searchResults, searchIndex } = useStore((state) => ({
    searchResults: state.searchResults,
    searchIndex: state.searchIndex,
  }));

  useEffect(() => {
    const timer = setTimeout(() => {
      const boxRanges = searchResults.filter((r) => r.boxId === box.id && !r.isTable);
      const titleDiv = document.getElementById(box.id);
      if (!titleDiv) return;

      // Clear previous highlights
      const existing = titleDiv.querySelectorAll('span.search-highlight');
      existing.forEach((el) => {
        const parent = el.parentNode;
        while (el.firstChild) parent.insertBefore(el.firstChild, el);
        parent.removeChild(el);
        parent.normalize();
      });

      if (boxRanges.length === 0) return;

      // Apply new highlights per block
      const byBlock = boxRanges.reduce((acc, r) => {
        (acc[r.blockKey] = acc[r.blockKey] || []).push(r);
        return acc;
      }, {});

      Object.entries(byBlock).forEach(([blockKey, ranges]) => {
        const blockRoot = titleDiv.querySelector(`[data-offset-key^="${blockKey}-"]`);
        if (!blockRoot) return;

        const walker = document.createTreeWalker(blockRoot, NodeFilter.SHOW_TEXT, null);
        const textNodes = [];
        let node, cum = 0;
        while ((node = walker.nextNode())) {
          const len = node.textContent.length;
          if (len > 0) {
            textNodes.push({ node, start: cum, end: cum + len });
            cum += len;
          }
        }
        if (textNodes.length === 0) return;

        ranges.forEach(({ start, end }) => {
          textNodes.forEach(({ node, start: ns, end: ne }) => {
            const overlapStart = Math.max(ns, start);
            const overlapEnd = Math.min(ne, end);
            if (overlapStart < overlapEnd) {
              const localStart = overlapStart - ns;
              const localEnd = overlapEnd - ns;
              const text = node.textContent;
              const before = text.slice(0, localStart);
              const match = text.slice(localStart, localEnd);
              const after = text.slice(localEnd);
              const parent = node.parentNode;
              const frag = document.createDocumentFragment();
              if (before) frag.appendChild(document.createTextNode(before));
              const span = document.createElement('span');
              span.className = 'search-highlight';
              span.style.backgroundColor = 'rgba(12, 180, 90, 0.35)'; // vert par défaut
              span.textContent = match;
              span.dataset.blockKey = blockKey;
              span.dataset.start = String(overlapStart);
              span.dataset.end = String(overlapEnd);
              frag.appendChild(span);
              if (after) frag.appendChild(document.createTextNode(after));
              parent.replaceChild(frag, node);
            }
          });
        });
      });

      // Mettre en surbrillance orange l'occurrence active
      const currentState = useStore.getState();
      const active = currentState.searchResults?.[currentState.searchIndex];
      if (active && active.boxId === box.id && !active.isTable) {
        const activeSpan = titleDiv.querySelector(
          `span.search-highlight[data-block-key="${active.blockKey}"][data-start="${active.start}"][data-end="${active.end}"]`
        );
        if (activeSpan) {
          activeSpan.style.backgroundColor = 'rgba(255, 165, 0, 0.5)'; // orange
          const rect = activeSpan.getBoundingClientRect();
          const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
          const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
          const isVisible = (
            rect.top < viewportHeight &&
            rect.bottom > 0 &&
            rect.left < viewportWidth &&
            rect.right > 0
          );
          if (!isVisible) {
            activeSpan.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [searchResults, searchIndex, box.id, editorState]);

  const {
    color: { style: color },
    ...rest
  } = configurationType;


  const write = (editorState) => {
    patchBoxContent(box.id, editorState);
    setEditorState(editorState);
  };

  // Écriture du menu « @ » : il passe une fonction de mise à jour pour lire
  // l'état courant. Même envoi que la frappe, sinon la variable insérée
  // n'atteint le brouillon collab qu'à la désélection du titre.
  const writeFromMenu = (next) => {
    setEditorState((prevEditorState) => {
      const resolved = typeof next === 'function' ? next(prevEditorState) : next;
      if (resolved !== prevEditorState) patchBoxContent(box.id, resolved);
      return resolved;
    });
  };


  const fontSizeTitle = configuration[box.type].fontSize;
  const titleLineHeight = calculateOptimalLineHeight(fontSizeTitle);
  const paddingTitle = gridConfiguration[box.type].paddingTitle;
  const titleIndex = displayTitleIndex(
    titleDisplayIndex(box.content),
    separator,
    titleTypeConfig,
    numberedTitleLevels(configuration),
    configuration.finalSeparatorTitle
  );
  // Niveau masqué ou titre hors numérotation : dans les deux cas il n'y a rien
  // à écrire, et le bloc du numéro n'a pas lieu d'être.
  const hasIndex = titleIndex !== '';
  const layout = getTitleLayout(configuration[box.type], {
    hasIndex,
    indexGap: paddingTitle,
  });

  // If in default container, focus on one click
  useEffect(() => {
    if (!drawMode && isSelected) {
      editorRef.current.focus();
    }
  }, [drawMode, isSelected])

  const menuAncestorIds = useMemo(() => [box.id], [box.id])
  const { fromViewer } = usePreviewContext();
  const isPdfMode = fromPdf || new URLSearchParams(window.location.search).has('generate_pdf');
  const showCollapseChevron = !drawMode && !isPdfMode && fromViewer !== viewerTypes.PREVIEW;

  return (
    <EditorProvider setEditorState={setEditorState} editorState={editorState} readOnly={!isSelected}>
      <div
        ref={boundingBoxRef}
        className="title-block-wrapper"
        style={{
          position: 'relative',
          width: box.width,
          marginTop: configuration[box.type].marginTop,
          marginBottom: configuration[box.type].marginBottom,
        }}
      >
        {showCollapseChevron && (
          <div className="container-collapse">
            <Tooltip
              title={box?.collapsed ? "Déplier le titre" : "Réduire le titre"}
              placement="bottom"
              mouseLeaveDelay={0}
              destroyOnHidden
            >
              <FontAwesomeIcon
                id="collapsed"
                icon={faChevronDown}
                className={box.collapsed ? 'title-collapse-icon--collapsed' : 'title-collapse-icon--expanded'}
                onClick={() => updateBox(box.id, (box) => {
                  box.collapsed = !box?.collapsed;
                })}
              />
            </Tooltip>
          </div>
        )}
        <div
          id={box.id}
          className="title-content-row"
          style={{
            ...getTitleStyle(
              configuration[box.type].backgroundColor,
              configuration[box.type].borders,
              configuration[box.type],
              color,
              rest
            ),
            ...getTitleTextStyle(configuration[box.type]),
            width: '100%',
            fontSize: fontSizeTitle,
            lineHeight: titleLineHeight,
            ...layout.row,
          }}
          onKeyDown={(e) => {
            if (editorState.getSelection().getHasFocus())
              e.stopPropagation()
          }}
        >
          {/* Sans numéro : on retire le bloc, pas seulement son texte, sinon
              son padding décalerait le titre. */}
          {hasIndex && (
            <div
              className="title-index"
              style={layout.index}
            >
              {titleIndex}
            </div>
          )}
          <div
            className="title-editor"
            style={{
              ...layout.text,
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}
          >
            <Editor
              stripPastedStyles={true}
              handleBeforeInput={(chars, editorState) => handleBeforeInput(
                {
                  chars,
                  editorState,
                  setEditorState
                }
              )}
              editorState={editorState}
              onChange={write}
              spellCheck={true}
              textAlignment={layout.textAlign}
              ref={editorRef}
              onFocus={(event) => {
                setBoxMovable(false);
                setEventManagerEnabled(false);
              }}
              readOnly={!isSelected}
            />
          </div>
        </div>
        {
          isSelected &&
          ReactDOM.createPortal(
            <ContextualMenu
              editorState={editorState}
              setEditorState={writeFromMenu}
              menuAncestorType={MENU_ANCESTORS.BOX.content}
              menuAncestorIds={menuAncestorIds}
              defaultSelection={defaultSelection}
              setDefaultSelection={setDefaultSelection}
            />,
            document.getElementById('SelectionVariablePortal')
          )
        }
        {
          isSelected && (
            <>
              <TitleComponent selectedBox={box} state={state}
                boxes={boxes}
                editorState={editorState}
                setEditorState={setEditorState}
                write={write}
                setDefaultSelection={setDefaultSelection}
              />
              <BoxActionsPanel />
            </>
          )
        }
      </div >
    </EditorProvider >
  );
};

const TitleComponent = ({ selectedBox, boxes, editorState, setEditorState, write, setDefaultSelection }) => {

  const {
    updateIndexTitle,
    updateBox,
    defaultConfiguration,
    toggleTitleNumbering,
    isTemplate,
  } = useStore(draft());
  const { t } = useTranslation();
  const boxId = selectedBox?.id;
  // Interrupteur sans effet quand aucun niveau n'est indexé : décocher un titre
  // ne retirerait un numéro ni à lui ni à ses sous-titres.
  const noLevelNumbered = !numberedTitleLevels(defaultConfiguration).some(Boolean);
  // La numérotation appartient à la charte du modèle : en proposal, on la
  // montre sans la laisser changer.
  const numberingDisabledReason = !isTemplate
    ? t('number-this-title-template-only')
    : noLevelNumbered
      ? t('number-this-title-disabled')
      : '';


  const handleAddAt = () => {
    const newEditorState = AddVariableInContentTextRichEditor(editorState, ' @');
    setEditorState(newEditorState);
  };

  const handleAddImageVariable = () => {
    const newEditorState = AddVariableInContentTextRichEditor(editorState, ' /');
    setDefaultSelection("img")
    setEditorState(newEditorState);
  };
  // Deux sections libellées comme le panel d'une box texte : les deux icônes
  // (@ / image) et le sélecteur de niveau étaient posés côte à côte sur une
  // ligne, sans un mot, et seul le survol disait ce qu'ils faisaient.
  return (
    <>
      <BoxFormControlsPortal title={t('title')}>
        <LabelledRow label={t('title-level')}>
          <NavbarSelector
            tooltipContent={t('title-level')}
            onSelect={(e) => selectLevelTitle(e, { boxId, updateBox, boxes, defaultConfiguration, updateIndexTitle, selectedBox })
            }
            selectedValue={selectedBox.type}
            options={TITLES.map(({ id, name }) => ({ label: name, value: id }))}
          />
        </LabelledRow>
        <LabelledRow label={t('number-this-title')}>
          <Tooltip title={numberingDisabledReason} placement="left">
            {/* Le span porte le survol : un contrôle antd désactivé n'émet plus
                d'événement de souris, et c'est justement là qu'il faut dire
                pourquoi il l'est. */}
            <span style={{ display: 'inline-flex' }}>
              <Switch
                size="small"
                disabled={!!numberingDisabledReason}
                checked={!selectedBox.unnumbered}
                onChange={() => toggleTitleNumbering(boxId)}
              />
            </span>
          </Tooltip>
        </LabelledRow>
      </BoxFormControlsPortal>
      <BoxFormControlsPortal title={t('insertion')}>
        {/* `write` et non `setEditorState` : l'emoji est le contenu final,
            il part tout de suite aux co-éditeurs et au brouillon. */}
        <InsertionControls
          editorState={editorState}
          setEditorState={write}
          onAddAt={() => handleAddAt()}
          onAddImage={() => handleAddImageVariable()}
        />
      </BoxFormControlsPortal>
    </>
  );
};

export default Title;
